"use client";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bed, Bath, Users, MapPin } from "lucide-react";
import type { Product } from "@/lib/api-client";
import { formatPrice, propertyTypeLabel, pluralPropertyLabel } from "@/components/booking/format";
import { useAppStore, currentView } from "@/lib/store";
import { useI18n } from "@/lib/i18n/provider";

function pluralize(count: number, singular: string): string {
  return count === 1 ? singular : singular + "s";
}

export function ProductCard({ product }: { product: Product }) {
  const openProduct = useAppStore((s) => s.openProduct);
  const { t, locale } = useI18n();
  const image = product.images?.[0]?.url;
  return (
    <Card
      className="group cursor-pointer overflow-hidden p-0 transition-all hover:shadow-md"
      onClick={() => openProduct(product.id)}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {image ? (
           
          <img
            src={image}
            alt={product.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-muted-foreground">
            {t("common.noImage")}
          </div>
        )}
        <Badge className="absolute left-3 top-3 bg-background/90 text-foreground">
          {propertyTypeLabel(product.propertyType, locale)}
        </Badge>
      </div>
      <div className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 font-semibold leading-tight">
            {product.title}
          </h3>
          <div className="shrink-0 font-semibold">
            {formatPrice(product.pricePerNight)}
            <span className="text-xs font-normal text-muted-foreground">
              {" "}
              {t("card.perNight")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span className="line-clamp-1">{product.location}</span>
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Bed className="h-4 w-4" /> {product.bedrooms}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="h-4 w-4" /> {product.bathrooms}
          </span>
          <span className="flex items-center gap-1">
            <Users className="h-4 w-4" /> {product.maxGuests}
          </span>
        </div>
      </div>
    </Card>
  );
}

// (pluralPropertyLabel is exported from format.ts for the browse view.)
export { pluralPropertyLabel };
